import { CoverageZonesApiAdapter } from '../adapters/CoverageZonesApiAdapter';
import { CountryModel } from '../models/Country';
import { CourierModel, ServiceModel } from '../models/Courier';
import {
    CoverageRulesResponse,
    CreateCoverageRulePayload,
    ReorderPayload,
    UpdateCoverageRulePayload,
} from '../models/CoverageRule';
import { Filters } from '../models/Filters';

export class Service {
    constructor(private apiAdapter: CoverageZonesApiAdapter) { }

    async getCouriers(filters: Filters = {}): Promise<CourierModel[]> {
        const { searchTerm } = filters;

        // Validación opcional
        if (searchTerm && searchTerm.length < 2) {
            throw new Error('El término de búsqueda debe tener al menos 2 caracteres');
        }

        const couriers = await this.apiAdapter.getCouriers();

        // Filtrado local si hay searchTerm
        if (searchTerm) {
            return couriers.filter((courier) =>
                courier.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return couriers;
    }

    async getCourierServices(courierId: string): Promise<ServiceModel[]> {
        if (!courierId) {
            throw new Error('El ID del courier es requerido');
        }

        return this.apiAdapter.getCourierServices(courierId);
    }

    // ========== MÉTODOS PARA REGLAS DE COBERTURA ==========

    async getCoverageRules(courierServiceId: number): Promise<CoverageRulesResponse> {
        if (!courierServiceId) {
            throw new Error('El ID del servicio de courier es requerido');
        }

        return this.apiAdapter.getCoverageRules(courierServiceId);
    }

    async createCoverageRule(
        courierServiceId: number,
        payload: CreateCoverageRulePayload
    ): Promise<void> {
        // Validaciones
        if (!courierServiceId) {
            throw new Error('El ID del servicio de courier es requerido');
        }

        if (!payload.country_id) {
            throw new Error('El país es requerido');
        }

        if (!payload.notes || payload.notes.trim() === '') {
            throw new Error('Las notas son requeridas');
        }

        // Validar rango de códigos postales
        if (payload.cp_from && payload.cp_to) {
            if (payload.cp_from > payload.cp_to) {
                throw new Error('El código postal "Desde" debe ser menor que "Hasta"');
            }
        }

        // Validar formato de códigos postales (básico)
        const cpRegex = /^[0-9]{4,6}$/;
        if (payload.cp_from && !cpRegex.test(payload.cp_from)) {
            throw new Error('Formato de código postal "Desde" inválido');
        }
        if (payload.cp_to && !cpRegex.test(payload.cp_to)) {
            throw new Error('Formato de código postal "Hasta" inválido');
        }

        return this.apiAdapter.createCoverageRule(courierServiceId, payload);
    }

    async updateCoverageRule(
        ruleId: number,
        payload: UpdateCoverageRulePayload
    ): Promise<void> {
        // Validaciones similares a create
        if (!ruleId) {
            throw new Error('El ID de la regla es requerido');
        }

        if (!payload.country_id) {
            throw new Error('El país es requerido');
        }

        if (!payload.notes || payload.notes.trim() === '') {
            throw new Error('Las notas son requeridas');
        }

        // Validar rango de códigos postales
        if (payload.cp_from && payload.cp_to) {
            if (payload.cp_from > payload.cp_to) {
                throw new Error('El código postal "Desde" debe ser menor que "Hasta"');
            }
        }

        // Validar formato de códigos postales
        const cpRegex = /^[0-9]{4,6}$/;
        if (payload.cp_from && !cpRegex.test(payload.cp_from)) {
            throw new Error('Formato de código postal "Desde" inválido');
        }
        if (payload.cp_to && !cpRegex.test(payload.cp_to)) {
            throw new Error('Formato de código postal "Hasta" inválido');
        }

        return this.apiAdapter.updateCoverageRule(ruleId, payload);
    }

    async deleteCoverageRule(ruleId: number): Promise<void> {
        if (!ruleId) {
            throw new Error('El ID de la regla es requerido');
        }

        return this.apiAdapter.deleteCoverageRule(ruleId);
    }

    async reorderCoverageRules(
        courierServiceId: number,
        payload: ReorderPayload
    ): Promise<void> {
        if (!courierServiceId) {
            throw new Error('El ID del servicio de courier es requerido');
        }

        if (!payload.rules_order || payload.rules_order.length === 0) {
            throw new Error('El orden de las reglas es requerido');
        }

        return this.apiAdapter.reorderCoverageRules(courierServiceId, payload);
    }

    async bulkImportCoverageRules(
        courierServiceId: number,
        file: File,
        countryId: number,
        typeRule: 'allow' | 'deny',
        paymentMethodId?: number | null,
        notes?: string | null,
        groupRanges: boolean = true
    ): Promise<void> {
        // Validaciones
        if (!courierServiceId) {
            throw new Error('El ID del servicio de courier es requerido');
        }

        if (!file) {
            throw new Error('El archivo es requerido');
        }

        // Validar tipo de archivo
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            'application/vnd.ms-excel', // .xls
        ];
        const allowedExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

        if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
            throw new Error('Solo se permiten archivos Excel (.xlsx, .xls)');
        }

        // Validar tamaño máximo (10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB en bytes
        if (file.size > maxSize) {
            throw new Error('El archivo excede el tamaño máximo de 10MB');
        }

        if (!countryId) {
            throw new Error('El país es requerido');
        }

        if (!typeRule || (typeRule !== 'allow' && typeRule !== 'deny')) {
            throw new Error('El tipo de regla debe ser "allow" o "deny"');
        }

        // Validar longitud de notas (máximo 1000 caracteres)
        if (notes && notes.length > 1000) {
            throw new Error('Las notas no pueden exceder 1000 caracteres');
        }

        // Crear FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('country_id', countryId.toString());
        formData.append('type_rule', typeRule);
        
        if (paymentMethodId) {
            formData.append('payment_method_id', paymentMethodId.toString());
        }
        
        if (notes && notes.trim()) {
            formData.append('notes', notes.trim());
        }
        
        formData.append('group_ranges', groupRanges.toString());

        return this.apiAdapter.bulkImportCoverageRules(courierServiceId, formData);
    }

    async exportCoverageRules(
        countryId: number,
        courierServiceId?: number
    ): Promise<string> {
        // Validaciones
        if (!countryId) {
            throw new Error('El ID del país es requerido');
        }

        if (courierServiceId && courierServiceId <= 0) {
            throw new Error('El ID del servicio de courier debe ser un número válido');
        }

        return this.apiAdapter.exportCoverageRules(countryId, courierServiceId);
    }

    // ========== MÉTODOS PARA COUNTRIES ==========
    async getCountries(): Promise<CountryModel[]> {
        const countries = await this.apiAdapter.getCountries();

        // Mapeo/normalización si es necesario
        return countries.map((c) => ({
            id: c.id,
            name: c.name,
            iso2: (c as any).iso2 ?? (c as any).code ?? '',
            iso3: (c as any).iso3 ?? '',
            phoneCode: (c as any).phoneCode ?? '',
            createdAt: (c as any).createdAt ?? '',
            updatedAt: (c as any).updatedAt ?? '',
        } as CountryModel));
    }
}

